import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, TrendingDown, BarChart3 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";
const FreightRatesArticle = () => {
  const {
    t
  } = useLanguage();
  const freightRatesData = [{
    month: t('month.jul'),
    rate: 2850,
    volume: 145
  }, {
    month: t('month.aug'),
    rate: 2650,
    volume: 142
  }, {
    month: t('month.sep'),
    rate: 2400,
    volume: 138
  }, {
    month: t('month.oct'),
    rate: 2200,
    volume: 135
  }];
  const shareArticle = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    // You could add a toast notification here
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{t('header.title')}</h1>
                <p className="text-blue-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <article className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-red-500">
              <TrendingDown className="h-3 w-3 mr-1" />
              {t('common.declining')}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.freight.title')}
            </h1>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{t('article.freight.date')}</span>
                <span className="mx-2">•</span>
                <span>{t('article.freight.readTime')}</span>
              </div>
              <Button onClick={shareArticle} variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                {t('common.share')}
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="European logistics freight containers" className="w-full rounded-lg shadow-lg sk-edit-loading h-[384px] object-cover" />
            
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              {t('article.freight.lead')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.freight.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.freight.section1.content')}
            </p>

            {/* Interactive Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.freight.chart.title')}
                </CardTitle>
                <CardDescription>{t('article.freight.chart.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={freightRatesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value, name) => [name === 'rate' ? `$${value}` : `${value}M TEU`, name === 'rate' ? t('chart.rate') : t('chart.volume')]} />
                    <Line yAxisId="left" type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={3} />
                    <Line yAxisId="right" type="monotone" dataKey="volume" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.freight.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.freight.section2.content')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {t('article.freight.keyStats.title')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-blue-800">-23%</div>
                  <div className="text-sm text-blue-600">{t('article.freight.keyStats.rateDecline')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-800">$2,200</div>
                  <div className="text-sm text-blue-600">{t('article.freight.keyStats.currentRate')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-800">135M</div>
                  <div className="text-sm text-blue-600">{t('article.freight.keyStats.volumeTEU')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-800">18+</div>
                  <div className="text-sm text-blue-600">{t('article.freight.keyStats.waitingShips')}</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.freight.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.freight.section3.content')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.freight.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.freight.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-blue-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a href="https://www.reuters.com/business/autos-transportation/european-logistics-firms-face-impact-falling-freight-rates-2025-10-21/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <span className="mr-2">📰</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: Reuters
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/article/train-connection">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.train.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/article/carbon-market">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.carbon.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>;
};
export default FreightRatesArticle;